// @ts-check
// Caixa de atendimento (/service/inbox, /service/inbox/:ticketId e /service/inbox/thread/:threadId; contrato 10 de
// atendimento, seção 24.2). Três colunas a partir de 1.280 px (lista, conversa e "Quem é", recolhível e lembrado no
// navegador), duas abaixo disso com o "Quem é" por cima e, no celular, uma tela por vez. Abas e filtros na URL, situação
// de quem atende no topo, "Registrar atendimento" (telefone ou presencial), atalhos J, K, R, N, A, T e E, o primeiro
// passo do módulo e os estados vazios. O professor sem departamento vai para as Consultas.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { getServiceDeskSettings, getServiceMe, listInbox, listServiceTeams } from '../../data/service-desk.js';
import { findManyRecords } from '../../data/rest.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { isEditableElement, readStorage, writeStorage } from '../../lib/utils.js';
import {
  SERVICE_INBOX_SHORTCUTS, adjacentInboxKey, inboxFiltersToQuery, inboxFiltersToSearch, inboxItemKey, inboxLayoutFor, inboxPathFor, readInboxFilters,
  resolveInboxShortcut, visibleInboxPanes, visibleInboxViews,
} from '../../lib/service-inbox-rules.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { usePolling } from '../conversations/conversations-state.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Dropdown } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { ServiceInboxList } from './service-inbox-list.js';
import { ServiceTicketPane } from './service-ticket-pane.js';
import { ServiceWhoIsPanel } from './service-who-is-panel.js';
import { AgentStatusMenu } from './service-agent-status.js';
import { ServiceFirstSteps } from './service-first-steps.js';
import { NewTicketDialog } from './service-dialogs.js';
import { ReplyTemplatesManager } from './service-reply-templates.js';
import { Modal } from '../../ui/modal.js';
import { showServiceError } from './service-display.js';
import { t } from '../../i18n/index.js';

const WHO_IS_STORAGE_KEY = 'appcrm.serviceInbox.whoIsOpen';
const LIST_POLLING_MILLISECONDS = 15_000;

/** Largura da janela, atualizada no redimensionamento (o layout da Caixa muda em 1.280 e 768 px). */
function useWindowWidth() {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const update = () => setWidth(window.innerWidth);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
}

/** Ajuda dos atalhos da Caixa (a ajuda geral "?" ganha esta seção na junção; Notas da F3). */
function InboxShortcutsHelp() {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  return html`<span class="service-menu-anchor" ref=${anchorRef}>
    <${LightIconButton} icon="IconKeyboard" size="medium" ariaLabel=${t('serviceDesk.shortcuts.title')} onClick=${() => setIsOpen(!isOpen)}
      data-testid="service-shortcuts" />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${260} placement="bottom-end" role="dialog">
      <div class="service-shortcuts-help">
        <strong>${t('serviceDesk.shortcuts.title')}</strong>
        <ul>${SERVICE_INBOX_SHORTCUTS.map((shortcut) => html`<li key=${shortcut.key}><kbd>${shortcut.key.toUpperCase()}</kbd><span>${t(`serviceDesk.shortcuts.actions.${shortcut.action}`)}</span></li>`)}</ul>
      </div>
    <//>
  </span>`;
}

/** @param {{ ticketId?: string | null, threadId?: string | null }} props */
export function ServiceInboxPage({ ticketId = null, threadId = null }) {
  const [me, setMe] = useState(/** @type {import('../../data/service-desk.js').ServiceMe | null} */ (null));
  const [meStatus, setMeStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));
  const [teams, setTeams] = useState(/** @type {import('../../data/service-desk.js').ServiceTeam[]} */ ([]));
  const [campuses, setCampuses] = useState(/** @type {Array<{ id: string, name: string }>} */ ([]));
  const [notifyFamilyDefault, setNotifyFamilyDefault] = useState(true);
  const [items, setItems] = useState(/** @type {import('../../data/service-desk.js').InboxItem[] | null} */ (null));
  const [counts, setCounts] = useState(/** @type {Record<string, number>} */ ({}));
  const [pageInfo, setPageInfo] = useState({ hasNextPage: false, endCursor: /** @type {string | null} */ (null) });
  const [listStatus, setListStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isWhoIsOpen, setIsWhoIsOpen] = useState(() => readStorage(WHO_IS_STORAGE_KEY) !== 'false');
  const [isWhoIsOverlayOpen, setIsWhoIsOverlayOpen] = useState(false);
  const [whoIsReloadKey, setWhoIsReloadKey] = useState(0);
  const [commandRequest, setCommandRequest] = useState(/** @type {{ action: string, nonce: number } | null} */ (null));
  const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);
  const [isPersonalRepliesOpen, setIsPersonalRepliesOpen] = useState(false);
  const [loadedTicketId, setLoadedTicketId] = useState(/** @type {string | null} */ (null));
  const [loadedThreadId, setLoadedThreadId] = useState(/** @type {string | null} */ (null));
  const width = useWindowWidth();
  const layout = inboxLayoutFor(width);
  const filters = readInboxFilters(searchParamsState.value, me ?? undefined);
  const [searchText, setSearchText] = useState(filters.q);
  const debouncedSearch = useDebouncedValue(searchText, 300);
  const selectedKey = ticketId ? `ticket:${ticketId}` : threadId ? `thread:${threadId}` : null;
  const queryKey = JSON.stringify(inboxFiltersToQuery(filters));

  useEffect(() => {
    document.title = t('serviceDesk.screens.inbox');
    getServiceMe()
      .then((data) => {
        // o professor sem departamento não tem Caixa: as consultas são a tela dele (seção 17.5)
        if (data?.isTeacher && (data.teams ?? []).length === 0) {
          navigate('/service/consultations', { replace: true });
          return;
        }
        setMe(data);
        setMeStatus('ready');
      })
      .catch(() => setMeStatus('error'));
    listServiceTeams().then(setTeams).catch(() => setTeams([]));
    getServiceDeskSettings().then((settings) => setNotifyFamilyDefault(settings?.transferNoticeDefault !== false)).catch(() => {});
    const campusObject = objectsByNameSingular.value.get('campus');
    if (campusObject && canReadObjectRecords(campusObject)) {
      findManyRecords(campusObject, { limit: 50 }).then((result) => setCampuses((result.records ?? []).map((record) => ({ id: record.id, name: record.name ?? '' })))).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (debouncedSearch !== filters.q) updateSearchParams(inboxFiltersToSearch({ q: debouncedSearch }));
  }, [debouncedSearch]);

  /** @param {{ append?: boolean }} [options] */
  const loadList = async ({ append = false } = {}) => {
    if (!me) return;
    try {
      const response = await listInbox(inboxFiltersToQuery(filters, append ? { startingAfter: pageInfo.endCursor } : {}));
      setItems((current) => (append ? [...(current ?? []), ...response.items] : response.items));
      setCounts(response.counts);
      setPageInfo({ hasNextPage: Boolean(response.pageInfo?.hasNextPage), endCursor: response.pageInfo?.endCursor ?? null });
      setListStatus('ready');
    } catch (error) {
      if (!append) setListStatus('error');
      else showServiceError(error);
    }
  };
  useEffect(() => {
    if (!me) return;
    setListStatus('loading');
    setItems(null);
    loadList();
  }, [queryKey, me?.canSeeAllStandardTeams]);
  usePolling(() => loadList(), LIST_POLLING_MILLISECONDS, listStatus === 'ready');

  const views = useMemo(() => visibleInboxViews(me ?? {}), [me?.canSeeAllStandardTeams]);
  const myTeams = (me?.teams ?? []).map((team) => ({ id: team.id, name: team.name }));
  const listItems = (items ?? []).map((item) => ({ key: inboxItemKey(item), item }));
  const search = searchParamsState.value.toString();

  /** @param {Partial<import('../../lib/service-inbox-rules.js').InboxFilters>} changes */
  const changeFilters = (changes) => updateSearchParams(inboxFiltersToSearch(changes));
  /** @param {string | null} key */
  const openKey = (key) => {
    if (!key) return;
    navigate(inboxPathFor(key, search ? `?${search}` : ''));
  };
  const closeSelection = () => navigate(`/service/inbox${search ? `?${search}` : ''}`);
  // a preferência guardada vale para a coluna; por cima da conversa (duas colunas e celular) o painel só abre quando a
  // pessoa pede, senão cobriria a conversa a cada atendimento aberto
  const isWhoIsColumn = layout === 'three';
  const isWhoIsShown = isWhoIsColumn ? isWhoIsOpen : isWhoIsOverlayOpen;
  const toggleWhoIs = () => {
    if (!isWhoIsColumn) {
      setIsWhoIsOverlayOpen(!isWhoIsOverlayOpen);
      return;
    }
    const next = !isWhoIsOpen;
    setIsWhoIsOpen(next);
    writeStorage(WHO_IS_STORAGE_KEY, next ? null : 'false');
  };

  // atalhos da Caixa: J e K andam na lista; R, N, A, T e E agem no atendimento aberto
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.defaultPrevented || document.querySelector('.modal-backdrop, [data-dropdown-surface]')) return;
      const action = resolveInboxShortcut(event, { isTyping: isEditableElement(/** @type {Element | null} */ (document.activeElement)) });
      if (!action) return;
      if (action === 'next' || action === 'previous') {
        event.preventDefault();
        openKey(adjacentInboxKey(listItems, selectedKey, action === 'next' ? 1 : -1));
        return;
      }
      if (!selectedKey) return;
      event.preventDefault();
      setCommandRequest({ action, nonce: Date.now() });
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [listItems.map((entry) => entry.key).join(','), selectedKey, search]);

  const panes = visibleInboxPanes(layout, { hasSelection: Boolean(selectedKey), isWhoIsOpen: isWhoIsShown });
  const header = html`<${PageHeader}
    icon="IconInbox"
    title=${t('serviceDesk.screens.inbox')}
    actions=${html`<div class="service-header-actions">
      ${me ? html`<${AgentStatusMenu} status=${me.agentStatus} onChanged=${(status) => {
        setMe({ ...me, agentStatus: status });
        loadList();
      }} />` : null}
      ${layout !== 'one' ? html`<${InboxShortcutsHelp} />` : null}
      <${LightIconButton} icon="IconMessage" size="medium" ariaLabel=${t('serviceDesk.replyTemplates.personalTitle')} onClick=${() => setIsPersonalRepliesOpen(true)}
        data-testid="service-personal-replies" />
      <${Button} title=${layout === 'one' ? undefined : t('serviceDesk.newTicket.open')} ariaLabel=${t('serviceDesk.newTicket.open')} icon="IconPlus" size="small" variant="secondary"
        onClick=${() => setIsNewTicketOpen(true)} data-testid="service-new-ticket" />
    </div>`}
  />`;

  if (meStatus === 'loading') {
    return html`<${PageCard} className="service-screen service-inbox-card" header=${header}>
      <div class="service-inbox-loading"><${Skeleton} height=${18} width=${240} /><${Skeleton} height=${120} /></div>
    <//>`;
  }
  if (meStatus === 'error' || !me) {
    return html`<${PageCard} className="service-screen service-inbox-card" header=${header}>
      <div class="service-empty" data-testid="service-inbox-error">
        <${EmptyState} icon="IconAlertTriangle" title=${t('serviceDesk.list.errorTitle')} subtitle=${t('serviceDesk.list.errorDescription')}
          action=${html`<${Button} title=${t('serviceDesk.list.retry')} size="small" variant="secondary" onClick=${() => window.location.reload()} />`} />
      </div>
    <//>`;
  }
  const hasNoTeam = me.teams.length === 0;
  return html`<${PageCard} className="service-screen service-inbox-card" header=${header}>
    <div class="service-inbox" data-layout=${layout} data-who-is=${panes.whoIsMode} data-has-selection=${selectedKey ? 'true' : undefined} data-testid="service-inbox">
      ${hasNoTeam && me.canSeeAllStandardTeams ? html`<${ServiceFirstSteps} teams=${teams} />` : null}
      ${hasNoTeam && !me.canSeeAllStandardTeams
        ? html`<div class="service-empty"><${EmptyState} icon="IconUsersGroup" title=${t('serviceDesk.list.noTeamTitle')} subtitle=${t('serviceDesk.list.noTeamDescription')} /></div>`
        : html`<div class="service-inbox-columns">
            ${panes.list
              ? html`<${ServiceInboxList}
                  filters=${filters}
                  views=${views}
                  counts=${counts}
                  items=${items}
                  status=${listStatus}
                  selectedKey=${selectedKey}
                  teams=${myTeams}
                  campuses=${campuses}
                  hasNextPage=${pageInfo.hasNextPage}
                  isLoadingMore=${isLoadingMore}
                  searchText=${searchText}
                  onSearchTextChange=${setSearchText}
                  onFiltersChange=${changeFilters}
                  onSelect=${(item) => openKey(inboxItemKey(item))}
                  onRetry=${() => loadList()}
                  onLoadMore=${async () => {
                    setIsLoadingMore(true);
                    await loadList({ append: true });
                    setIsLoadingMore(false);
                  }}
                />`
              : null}
            ${panes.conversation
              ? selectedKey
                ? html`<${ServiceTicketPane}
                    key=${selectedKey}
                    ticketId=${ticketId}
                    threadId=${threadId}
                    me=${me}
                    teams=${teams}
                    isWhoIsOpen=${isWhoIsShown}
                    onToggleWhoIs=${toggleWhoIs}
                    onBack=${layout === 'one' ? closeSelection : null}
                    onChanged=${() => {
                      loadList();
                      getServiceMe().then(setMe).catch(() => {});
                    }}
                    onWhoIsReload=${() => setWhoIsReloadKey((value) => value + 1)}
                    onTicketLoaded=${(detail) => {
                      setLoadedTicketId(detail?.ticket?.id ?? null);
                      setLoadedThreadId(detail?.threads?.[0]?.id ?? threadId ?? null);
                    }}
                    commandRequest=${commandRequest}
                    notifyFamilyDefault=${notifyFamilyDefault}
                  />`
                : html`<div class="service-pane service-pane-empty" data-testid="service-no-selection">
                    <${EmptyState} icon="IconInbox" title=${t('serviceDesk.pane.noSelectionTitle')} subtitle=${t('serviceDesk.pane.noSelection')} />
                  </div>`
              : null}
            ${panes.whoIs && selectedKey
              ? html`${panes.whoIsMode !== 'column' ? html`<div class="service-who-backdrop" onClick=${toggleWhoIs}></div>` : null}
                <${ServiceWhoIsPanel}
                  key=${`who-${selectedKey}`}
                  ticketId=${ticketId}
                  threadId=${ticketId ? (loadedTicketId === ticketId ? loadedThreadId : null) : threadId}
                  mode=${/** @type {any} */ (panes.whoIsMode)}
                  onClose=${toggleWhoIs}
                  onChanged=${() => loadList()}
                  reloadKey=${whoIsReloadKey}
                />`
              : null}
          </div>`}
    </div>
    ${isPersonalRepliesOpen
      ? html`<${Modal} title=${t('serviceDesk.replyTemplates.personalTitle')} subtitle=${t('serviceDesk.replyTemplates.personalDescription')} confirmLabel=${t('serviceDesk.common.close')}
          hideCancel=${true} onClose=${() => setIsPersonalRepliesOpen(false)} onConfirm=${() => setIsPersonalRepliesOpen(false)}>
          <${ReplyTemplatesManager} scope="PERSONAL" canEdit=${true} />
        <//>`
      : null}
    ${isNewTicketOpen ? html`<${NewTicketDialog} teams=${teams} onClose=${() => setIsNewTicketOpen(false)} onCreated=${(ticket) => {
      setIsNewTicketOpen(false);
      loadList();
      if (ticket?.id) navigate(`/service/inbox/${ticket.id}`);
    }} />` : null}
  <//>`;
}
